import { auth } from '../config/firebase';
import { ShopLocation, Place, RateLimitError } from '../types';

const API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

class ShopLocationService {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken();
    const url = `${API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Get all locations for a shop
   */
  async getShopLocations(
    shopId: string,
    page: number = 0,
    size: number = 100,
    sort: string = 'sortOrder',
    direction: 'ASC' | 'DESC' = 'ASC'
  ): Promise<ShopLocation[]> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sort,
      direction
    });
    return this.request<ShopLocation[]>(
      `/api/v1/shops/${shopId}/locations?${params.toString()}`
    );
  }

  /**
   * Get a specific location by ID
   */
  async getShopLocation(shopId: string, locationId: string): Promise<ShopLocation> {
    return this.request<ShopLocation>(
      `/api/v1/shops/${shopId}/locations/${locationId}`
    );
  }

  /**
   * Create a new shop location
   */
  async createShopLocation(shopId: string, location: Partial<ShopLocation>, images?: File[]): Promise<ShopLocation> {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    // Backend expects multipart/form-data with metadata as a form parameter
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(location));

    // Append images if provided
    if (images && images.length > 0) {
      images.forEach(file => {
        formData.append('images', file);
      });
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/locations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type - let browser set it with boundary for multipart
      },
      body: formData
    });

    if (!response.ok) {
      // Handle rate limit errors specially
      if (response.status === 429) {
        const errorData = await response.json();
        throw new RateLimitError({
          resourceType: errorData.resourceType || 'SHOP_LOCATIONS',
          currentUsage: errorData.currentUsage || 0,
          maxAllowed: errorData.maxAllowed || 0,
          tierType: errorData.tierType || 'UNKNOWN',
          message: errorData.message || 'Location limit exceeded'
        });
      }
      const errorText = await response.text();
      throw new Error(`Failed to create location: ${response.status} ${errorText}`);
    }

    return response.json();
  }

  /**
   * Update a shop location
   */
  async updateShopLocation(
    shopId: string,
    locationId: string,
    updates: Partial<ShopLocation>
  ): Promise<ShopLocation> {
    return this.request<ShopLocation>(
      `/api/v1/shops/${shopId}/locations/${locationId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    );
  }

  /**
   * Delete a shop location (soft delete)
   */
  async deleteShopLocation(shopId: string, locationId: string): Promise<void> {
    const token = await this.getAuthToken();
    const url = `${API_BASE_URL}/api/v1/shops/${shopId}/locations/${locationId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      // Preserve the response object in the error for error handling in the UI
      const error: any = new Error(`HTTP ${response.status}`);
      error.response = {
        status: response.status,
        headers: {
          'x-error-message': response.headers.get('x-error-message') || response.headers.get('X-Error-Message')
        }
      };
      throw error;
    }
  }

  /**
   * Add a place to a location
   */
  async addPlace(shopId: string, locationId: string, place: Partial<Place>, images?: File[]): Promise<Place> {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    // Backend expects multipart/form-data with metadata as a form parameter
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(place));

    // Append images if provided
    if (images && images.length > 0) {
      images.forEach(file => {
        formData.append('images', file);
      });
    }

    const response = await fetch(
      `${API_BASE_URL}/api/v1/shops/${shopId}/locations/${locationId}/places`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let browser set it with boundary for multipart
        },
        body: formData
      }
    );

    if (!response.ok) {
      // Handle rate limit errors specially
      if (response.status === 429) {
        const errorData = await response.json();
        throw new RateLimitError({
          resourceType: errorData.resourceType || 'SHOP_PLACES',
          currentUsage: errorData.currentUsage || 0,
          maxAllowed: errorData.maxAllowed || 0,
          tierType: errorData.tierType || 'UNKNOWN',
          message: errorData.message || 'Place limit exceeded'
        });
      }
      const errorText = await response.text();
      throw new Error(`Failed to add place: ${response.status} ${errorText}`);
    }

    // Backend returns the full ShopLocation, extract the newly created place
    const shopLocation: ShopLocation = await response.json();
    const createdPlace = shopLocation.places?.find(p => p.name === place.name);
    if (!createdPlace) {
      throw new Error('Place was created but not found in response');
    }
    return createdPlace;
  }

  /**
   * Update a place in a location
   */
  async updatePlace(
    shopId: string,
    locationId: string,
    placeId: string,
    updates: Partial<Place>
  ): Promise<Place> {
    return this.request<Place>(
      `/api/v1/shops/${shopId}/locations/${locationId}/places/${placeId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    );
  }

  /**
   * Delete a place from a location
   */
  async deletePlace(shopId: string, locationId: string, placeId: string): Promise<void> {
    return this.request<void>(
      `/api/v1/shops/${shopId}/locations/${locationId}/places/${placeId}`,
      {
        method: 'DELETE'
      }
    );
  }

  /**
   * Move a place from one location to another
   */
  async movePlace(
    shopId: string,
    placeId: string,
    fromLocationId: string,
    toLocationId: string
  ): Promise<Place> {
    return this.request<Place>(
      `/api/v1/shops/${shopId}/places/${placeId}/move`,
      {
        method: 'PUT',
        body: JSON.stringify({
          fromLocationId,
          toLocationId
        })
      }
    );
  }

  /**
   * Get location image URL
   */
  getLocationImageUrl(
    shopId: string,
    locationId: string,
    imageId: string,
    size?: 'thumb_300' | 'thumb_800'
  ): string {
    const sizeParam = size ? `?size=${size}` : '';
    return `${API_BASE_URL}/api/v1/shops/${shopId}/locations/${locationId}/images/${imageId}${sizeParam}`;
  }

  /**
   * Get place image URL
   */
  getPlaceImageUrl(
    shopId: string,
    locationId: string,
    placeId: string,
    imageId: string,
    size?: 'thumb_300' | 'thumb_800'
  ): string {
    const sizeParam = size ? `?size=${size}` : '';
    return `${API_BASE_URL}/api/v1/shops/${shopId}/locations/${locationId}/places/${placeId}/images/${imageId}${sizeParam}`;
  }

  /**
   * Upload location image
   */
  async uploadLocationImage(
    shopId: string,
    locationId: string,
    file: File
  ): Promise<any> {
    const token = await this.getAuthToken();
    const url = `${API_BASE_URL}/api/v1/shops/${shopId}/locations/${locationId}/images`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  /**
   * Upload place image
   */
  async uploadPlaceImage(
    shopId: string,
    locationId: string,
    placeId: string,
    file: File
  ): Promise<any> {
    const token = await this.getAuthToken();
    const url = `${API_BASE_URL}/api/v1/shops/${shopId}/locations/${locationId}/places/${placeId}/images`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  /**
   * Delete location image
   */
  async deleteLocationImage(
    shopId: string,
    locationId: string,
    imageId: string
  ): Promise<void> {
    return this.request<void>(
      `/api/v1/shops/${shopId}/locations/${locationId}/images/${imageId}`,
      {
        method: 'DELETE'
      }
    );
  }

  /**
   * Delete place image
   */
  async deletePlaceImage(
    shopId: string,
    locationId: string,
    placeId: string,
    imageId: string
  ): Promise<void> {
    return this.request<void>(
      `/api/v1/shops/${shopId}/locations/${locationId}/places/${placeId}/images/${imageId}`,
      {
        method: 'DELETE'
      }
    );
  }

  /**
   * Fetch location image headers only (lightweight check for content type)
   */
  async fetchLocationImageHeaders(
    shopId: string,
    locationId: string,
    imageId: string,
    size?: 'thumb_300' | 'thumb_800'
  ): Promise<Response> {
    const token = await this.getAuthToken();
    const url = this.getLocationImageUrl(shopId, locationId, imageId, size);

    return fetch(url, {
      method: 'HEAD',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
  }

  /**
   * Fetch location image with authentication (returns Response for blob handling).
   */
  async fetchLocationImage(
    shopId: string,
    locationId: string,
    imageId: string,
    size?: 'thumb_300' | 'thumb_800'
  ): Promise<Response> {
    const token = await this.getAuthToken();
    const url = this.getLocationImageUrl(shopId, locationId, imageId, size);

    return fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
  }

  /**
   * Fetch place image headers only (lightweight check for content type)
   */
  async fetchPlaceImageHeaders(
    shopId: string,
    locationId: string,
    placeId: string,
    imageId: string,
    size?: 'thumb_300' | 'thumb_800'
  ): Promise<Response> {
    const token = await this.getAuthToken();
    const url = this.getPlaceImageUrl(shopId, locationId, placeId, imageId, size);

    return fetch(url, {
      method: 'HEAD',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
  }

  /**
   * Fetch place image with authentication (returns Response for blob handling).
   */
  async fetchPlaceImage(
    shopId: string,
    locationId: string,
    placeId: string,
    imageId: string,
    size?: 'thumb_300' | 'thumb_800'
  ): Promise<Response> {
    const token = await this.getAuthToken();
    const url = this.getPlaceImageUrl(shopId, locationId, placeId, imageId, size);

    return fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
  }

  /**
   * Rotate a location image
   */
  async rotateLocationImage(
    shopId: string,
    locationId: string,
    imageId: string,
    degrees: number
  ): Promise<void> {
    return this.request<void>(
      `/api/v1/shops/${shopId}/locations/${locationId}/images/${imageId}/rotate`,
      {
        method: 'POST',
        body: JSON.stringify({ degrees })
      }
    );
  }

  /**
   * Crop a location image
   */
  async cropLocationImage(
    shopId: string,
    locationId: string,
    imageId: string,
    cropArea: { x: number; y: number; width: number; height: number }
  ): Promise<void> {
    return this.request<void>(
      `/api/v1/shops/${shopId}/locations/${locationId}/images/${imageId}/crop`,
      {
        method: 'POST',
        body: JSON.stringify(cropArea)
      }
    );
  }

  /**
   * Rotate a place image
   */
  async rotatePlaceImage(
    shopId: string,
    locationId: string,
    placeId: string,
    imageId: string,
    degrees: number
  ): Promise<void> {
    return this.request<void>(
      `/api/v1/shops/${shopId}/locations/${locationId}/places/${placeId}/images/${imageId}/rotate`,
      {
        method: 'POST',
        body: JSON.stringify({ degrees })
      }
    );
  }

  /**
   * Crop a place image
   */
  async cropPlaceImage(
    shopId: string,
    locationId: string,
    placeId: string,
    imageId: string,
    cropArea: { x: number; y: number; width: number; height: number }
  ): Promise<void> {
    return this.request<void>(
      `/api/v1/shops/${shopId}/locations/${locationId}/places/${placeId}/images/${imageId}/crop`,
      {
        method: 'POST',
        body: JSON.stringify(cropArea)
      }
    );
  }
}

export const shopLocationService = new ShopLocationService();
